import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Auth par cookie httpOnly (même origine): le navigateur envoie le cookie automatiquement
axios.defaults.withCredentials = true;

// Traduit une erreur axios en message clair pour l'utilisateur (réseau/serveur vs. détail backend).
const authErrorMessage = (error, fallback) => {
  // Aucune réponse reçue → serveur injoignable (réseau, CORS, 502/503 sans corps JSON)
  if (!error.response) {
    return 'Service momentanément indisponible. Vérifiez votre connexion et réessayez dans quelques instants.';
  }
  const status = error.response.status;
  if (status >= 500 || status === 502 || status === 503 || status === 504) {
    return 'Le serveur est momentanément indisponible. Réessayez dans quelques instants.';
  }
  const detail = error.response.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    const map = {
      'Email already registered': 'Cet e-mail est déjà utilisé. Essayez de vous connecter.',
      'Invalid credentials': 'E-mail ou mot de passe incorrect.',
    };
    return map[detail] || detail;
  }
  return fallback;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Si retour du callback OAuth Google (session_id dans le fragment), laisser AuthCallback gérer.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: authErrorMessage(error, 'Échec de la connexion. Réessayez.') };
    }
  };

  const register = async (email, password, full_name, role = 'doctor') => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, full_name, role });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: authErrorMessage(error, 'Échec de l’inscription. Réessayez.') };
    }
  };

  // Redirige vers Emergent Auth (Google). NE PAS coder en dur l'URL / pas de fallback.
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Échange le session_id Google contre notre session (cookie JWT), côté backend.
  const loginWithGoogleSession = useCallback(async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/session`, {}, {
        headers: { 'X-Session-ID': sessionId },
      });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Échec de la connexion Google' };
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, loginWithGoogle, loginWithGoogleSession, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
